import FitText from './FitText'
import Portrait from '../Portrait'
import { abbreviateName, displayPosition, positionFill } from './positions'
import type { PlayerSummary } from '../../types/protocol'
import type { PickState } from '../../types/protocol'

// One cell of the board: exactly two lines, abbreviated name over
// "POS · TEAM · BYE n". That treatment is fixed by the Phase 9 spec — the
// cells are ~2x the CSS-px size of the old 1080p figures but occupy the same
// *physical* area on a same-diagonal TV, so there is no more room for a full
// name or a third line than there ever was.
//
// The bye is what displaced #overall from that line. The pick number is the
// one datum on the cell you can also read off the grid's own geometry, and the
// bye is the only one that changes a decision in real time — so the number goes
// to a chip in the cell's top-right corner and the meta line spends its width
// on the bye instead.
//
// truncate is the safety net for the long tail ("A. Smith-Njigba"): the cell
// must never grow and push the grid off screen.
//
// The portrait is the one thing added since, and it is sized against that
// constraint rather than in spite of it: a 3:4 crop off the cell's own height
// (see PLATE.tile) takes ~28% of the name's width, which abbreviateName and
// truncate between them absorb. It is deliberately not square — the ~37% that
// would cost is the difference between "M. Harrison" fitting and not.

interface Props {
  pick: PickState | undefined
  player: PlayerSummary | null
  isCurrent: boolean
}

export default function PickCell({ pick, player, isCurrent }: Props) {
  // No pick generated for this round/team combination. The pre-generated grid
  // makes this unreachable in a healthy draft; render dead space, not a crash.
  if (!pick) return <div className="rounded-[0.5vh] bg-surface-1/40" />

  return (
    // data-overall is how the pick flash finds this cell to fly its card into —
    // the overlay is not in the grid's React tree, so it queries the document.
    // Not dead markup; see useFlightToCell.
    <div className="relative min-w-0" data-overall={pick.overall}>
      <CellBody pick={pick} player={player} />
      {isCurrent && (
        // Separate overlay so the pulse animates the ring alone — pulsing the
        // cell itself would strobe the text it sits behind.
        <div className="animate-board-pulse pointer-events-none absolute inset-0 rounded-[0.5vh] border-[0.35vh] border-signal" />
      )}
    </div>
  )
}

function CellBody({ pick, player }: { pick: PickState; player: PlayerSummary | null }) {
  if (pick.status === 'skipped') {
    return (
      <div className="flex h-full flex-col justify-center overflow-hidden rounded-[0.5vh] border-[0.2vh] border-dashed border-line-strong px-[0.35vw] py-[0.45vh]">
        <p className="text-board-name font-display truncate leading-[1.1] font-semibold text-ink-lo">
          Skipped
        </p>
        <p className="text-board-meta truncate leading-[1.15] font-semibold text-ink-lo tabular-nums">
          #{pick.overall}
        </p>
      </div>
    )
  }

  if (pick.status === 'made' && player) {
    return (
      // `relative` has to live here rather than on the PickCell wrapper: the
      // chip is anchored to the filled card, and anchoring it to the outer box
      // would let it sit outside the card's own rounded corner.
      <div
        className={`${positionFill(player.position)} text-pos-ink relative flex h-full items-stretch gap-[0.3vw] overflow-hidden rounded-[0.5vh] px-[0.35vw] py-[0.45vh] shadow-[inset_0_0.15vh_0_rgb(255_255_255/0.18)]`}
      >
        <Portrait
          fullName={player.full_name}
          position={player.position}
          nflTeam={player.nfl_team}
          src={player.portrait_url}
          jerseyNumber={player.jersey_number}
          size="tile"
          // Same reason as PickFlash: the plate's usual dark surface would read
          // as a sticker on the position fill, where a window reads as part of
          // the card.
          className="bg-pos-ink/25 shadow-[inset_0_0_0_0.2vh_rgb(0_0_0/0.18)]"
        />
        <div className="flex min-w-0 flex-1 flex-col justify-center">
          {/* Both lines shrink to fit rather than clipping — see FitText. That
              is what lets the meta line carry the bye at all: as a clipped line
              it could only ever have shown "RB · I… · BYE 13" or dropped the
              number, and the number is the whole point.

              `truncate` stays as the floor's safety net, not as the mechanism.
              FitText stops shrinking at MIN_SCALE, and past that point an
              ellipsis beats a hard cut mid-character. On any real board the
              text fits and it never shows.

              pr- reserves the chip's footprint on the name's line, since the
              chip overlays that corner. It comes out of the box FitText
              measures against, so a long name shrinks to clear the number
              instead of sliding under it. */}
          <p className="text-board-name font-display truncate pr-[1.4vw] leading-[1.1] font-bold">
            <FitText text={abbreviateName(player.full_name, player.position)} />
          </p>
          <p className="text-board-meta truncate leading-[1.15] font-bold tabular-nums opacity-80">
            <FitText text={metaLine(player)} />
          </p>
        </div>
        <span className="text-board-chip absolute top-[0.15vh] right-[0.25vw] font-bold tabular-nums opacity-55">
          {pick.overall}
        </span>
      </div>
    )
  }

  // Made but the pool has not resolved the player yet, or still pending.
  return (
    <div className="flex h-full flex-col justify-center overflow-hidden rounded-[0.5vh] bg-surface-1 px-[0.35vw] py-[0.45vh] shadow-panel">
      <p className="text-board-meta truncate leading-[1.15] font-semibold text-ink-lo tabular-nums">
        #{pick.overall}
      </p>
    </div>
  )
}

// A free agent has no team and so no bye, and nothing has a bye until the first
// sync that resolves all 32. Both drop the segment rather than render a dash —
// a placeholder repeated across a wall of cells reads as data.
function metaLine(player: PlayerSummary): string {
  const head = `${displayPosition(player.position)} · ${player.nfl_team ?? 'FA'}`
  return player.bye_week != null ? `${head} · BYE ${player.bye_week}` : head
}
